import {
  createInitialNationStates,
  createInitialRegionControllers,
  isNationStateRecord,
  isRegionControllerRecord,
  resolveEconomyTurn,
  type NationStateRecord,
  type RegionControllerRecord,
} from './economy'
import {
  createInitialConstructionState,
  isConstructionState,
  progressConstruction,
  type ConstructionProject,
  type ConstructionState,
} from './construction'
import {
  createInitialResearchState,
  isNationResearchRecord,
  progressResearchTurn,
  type NationResearchRecord,
  type ResearchCompletion,
} from './research'
import {
  createInitialSpaceProgramState,
  isNationSpaceProgramRecord,
  progressSpaceProgramTurn,
  type NationSpaceProgramRecord,
  type SpaceMissionCompletion,
} from './spaceProgram'
import {
  applyStrategicAiDecisions,
  type StrategicAiAction,
} from './strategicAi'
import {
  appendStrategicEvents,
  createInitialStrategicLog,
  isStrategicLogState,
  type StrategicEventDraft,
  type StrategicLogState,
} from './strategicLog'
import {
  createGameSettings,
  isGameSettings,
  type AiDifficulty,
  type GameSettings,
} from './gameSettings'
import {
  isActiveMajorPowerId,
  type ActiveMajorPowerId,
} from '../data/regions'
import { ACTIVE_MAJOR_POWER_IDS } from '../data/nations'

export const GAME_STATE_SCHEMA_VERSION = 6 as const

export type GameStatus = 'setup' | 'running' | 'won' | 'lost'

export interface GameState {
  schemaVersion: typeof GAME_STATE_SCHEMA_VERSION
  turn: number
  status: GameStatus
  playerNationId: ActiveMajorPowerId | null
  winnerNationId: ActiveMajorPowerId | null
  settings: GameSettings
  nations: NationStateRecord
  regionControllers: RegionControllerRecord
  construction: ConstructionState
  research: NationResearchRecord
  spaceProgram: NationSpaceProgramRecord
  strategicLog: StrategicLogState
}

export interface EndTurnResult {
  state: GameState
  advanced: boolean
  reason?: 'game-not-running' | 'stale-turn'
  completedConstruction: ConstructionProject[]
  completedResearch: ResearchCompletion[]
  completedSpaceMissions: SpaceMissionCompletion[]
  aiActions: StrategicAiAction[]
}

export function createInitialGameState(): GameState {
  const regionControllers = createInitialRegionControllers()
  const construction = createInitialConstructionState()

  return {
    schemaVersion: GAME_STATE_SCHEMA_VERSION,
    turn: 1,
    status: 'setup',
    playerNationId: null,
    winnerNationId: null,
    settings: createGameSettings(),
    nations: createInitialNationStates(
      regionControllers,
      construction.facilities,
    ),
    regionControllers,
    construction,
    research: createInitialResearchState(),
    spaceProgram: createInitialSpaceProgramState(),
    strategicLog: createInitialStrategicLog(),
  }
}

export function startNewGame(
  playerNationId: ActiveMajorPowerId = 'starsea',
  aiDifficulty: AiDifficulty = 'standard',
): GameState {
  if (!isActiveMajorPowerId(playerNationId)) {
    throw new Error('该国家尚未在当前剧本中启用。')
  }

  const regionControllers = createInitialRegionControllers()
  const construction = createInitialConstructionState()

  return {
    schemaVersion: GAME_STATE_SCHEMA_VERSION,
    turn: 1,
    status: 'running',
    playerNationId,
    winnerNationId: null,
    settings: createGameSettings(aiDifficulty),
    nations: createInitialNationStates(
      regionControllers,
      construction.facilities,
    ),
    regionControllers,
    construction,
    research: createInitialResearchState(),
    spaceProgram: createInitialSpaceProgramState(),
    strategicLog: createInitialStrategicLog(),
  }
}

export function endTurn(state: GameState, expectedTurn: number): EndTurnResult {
  if (state.status !== 'running') {
    return blockedTurn(state, 'game-not-running')
  }

  if (state.turn !== expectedTurn) {
    return blockedTurn(state, 'stale-turn')
  }

  const aiTurn = applyStrategicAiDecisions(state)
  const aiEventDrafts = aiTurn.actions.map((action) =>
    aiActionToEvent(action, state.turn),
  )
  const decisionState: GameState = {
    ...aiTurn.state,
    strategicLog: appendStrategicEvents(
      aiTurn.state.strategicLog,
      aiEventDrafts,
    ),
  }

  const constructionProgress = progressConstruction(
    decisionState.construction,
  )
  const nations = resolveEconomyTurn(
    decisionState.nations,
    decisionState.regionControllers,
    constructionProgress.construction.facilities,
  )
  const researchProgress = progressResearchTurn(
    decisionState.research,
    Object.fromEntries(
      ACTIVE_MAJOR_POWER_IDS.map((id) => [
        id,
        nations[id].economy.capacity.research,
      ]),
    ) as Record<ActiveMajorPowerId, number>,
  )
  const spaceProgress = progressSpaceProgramTurn(
    decisionState.spaceProgram,
  )
  const victoryCompletion = spaceProgress.completed.find(
    (completion) =>
      completion.missionId === 'crewed-lunar-landing-return',
  )
  const winnerNationId =
    victoryCompletion?.nationId ?? decisionState.winnerNationId
  const status: GameStatus =
    winnerNationId === null
      ? decisionState.status
      : winnerNationId === decisionState.playerNationId
        ? 'won'
        : 'lost'

  const completionEvents: StrategicEventDraft[] = [
    ...constructionProgress.completed.map((project) => ({
      turn: state.turn,
      nationId: project.nationId,
      type: 'construction-complete' as const,
      regionId: project.regionId,
      facilityId: project.facilityId,
    })),
    ...researchProgress.completed.map((completion) => ({
      turn: state.turn,
      nationId: completion.nationId,
      type: 'research-complete' as const,
      technologyId: completion.technologyId,
    })),
    ...spaceProgress.completed.map((completion) => ({
      turn: state.turn,
      nationId: completion.nationId,
      type: 'space-mission-complete' as const,
      missionId: completion.missionId,
    })),
  ]

  if (victoryCompletion) {
    completionEvents.push({
      turn: state.turn,
      nationId: victoryCompletion.nationId,
      type: 'victory',
      missionId: victoryCompletion.missionId,
    })
  }

  return {
    state: {
      ...decisionState,
      turn: decisionState.turn + 1,
      status,
      winnerNationId,
      construction: constructionProgress.construction,
      nations,
      research: researchProgress.research,
      spaceProgram: spaceProgress.spaceProgram,
      strategicLog: appendStrategicEvents(
        decisionState.strategicLog,
        completionEvents,
      ),
    },
    advanced: true,
    completedConstruction: constructionProgress.completed,
    completedResearch: researchProgress.completed,
    completedSpaceMissions: spaceProgress.completed,
    aiActions: aiTurn.actions,
  }
}

export function isGameState(value: unknown): value is GameState {
  if (!isRecord(value)) {
    return false
  }

  const validStatus =
    value.status === 'setup' ||
    value.status === 'running' ||
    value.status === 'won' ||
    value.status === 'lost'

  const validNationId =
    value.playerNationId === null || isActiveMajorPowerId(value.playerNationId)
  const validWinnerId =
    value.winnerNationId === null || isActiveMajorPowerId(value.winnerNationId)

  return (
    value.schemaVersion === GAME_STATE_SCHEMA_VERSION &&
    Number.isInteger(value.turn) &&
    typeof value.turn === 'number' &&
    value.turn >= 1 &&
    validStatus &&
    validNationId &&
    validWinnerId &&
    isGameSettings(value.settings) &&
    isNationStateRecord(value.nations) &&
    isRegionControllerRecord(value.regionControllers) &&
    isConstructionState(value.construction) &&
    isNationResearchRecord(value.research) &&
    isNationSpaceProgramRecord(value.spaceProgram) &&
    isStrategicLogState(value.strategicLog)
  )
}

function blockedTurn(
  state: GameState,
  reason: 'game-not-running' | 'stale-turn',
): EndTurnResult {
  return {
    state,
    advanced: false,
    reason,
    completedConstruction: [],
    completedResearch: [],
    completedSpaceMissions: [],
    aiActions: [],
  }
}

function aiActionToEvent(
  action: StrategicAiAction,
  turn: number,
): StrategicEventDraft {
  if (action.type === 'research-start') {
    return {
      turn,
      nationId: action.nationId,
      type: 'research-start',
      technologyId: action.technologyId,
    }
  }

  if (action.type === 'construction-start') {
    return {
      turn,
      nationId: action.nationId,
      type: 'construction-start',
      regionId: action.regionId,
      facilityId: action.facilityId,
    }
  }

  return {
    turn,
    nationId: action.nationId,
    type: 'space-mission-start',
    missionId: action.missionId,
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
